import { cn, Skeleton } from '@atlas/ui-primitives'

/**
 * Props for the {@link ListSkeleton} component.
 *
 * @property rows - Number of placeholder rows (default 4) — hints at the list to come
 * @property label - Names what is loading for a reader who cannot see the pulse; visually hidden
 * @property className - Optional additional Tailwind classes for the container
 */
export interface ListSkeletonProps {
	rows?: number
	label?: string
	className?: string
}

/**
 * Shaped list/table loading placeholder — rows, not a bare spinner.
 *
 * @remarks
 * - **Accessibility:** `aria-busy` while shown, and no role. A live region would
 *   announce on every load and refetch through the same polite channel the shell
 *   reserves for real outcomes, so the placeholder stays silent and only describes
 *   itself to a reader who navigates into it. The label rides as visually-hidden
 *   text rather than `aria-label`, which a role-less container does not support.
 * - **Styling:** rows are the `Skeleton` primitive — design tokens only.
 *
 * @example
 * ```tsx
 * {isLoading ? <ListSkeleton rows={5} label="Loading diagnostics" /> : <Table … />}
 * ```
 *
 * @see packages/ui-components/src/list-skeleton/list-skeleton.stories.tsx for visual examples
 */
export function ListSkeleton({ rows = 4, label = 'Loading', className }: ListSkeletonProps) {
	return (
		<div aria-busy="true" className={cn('space-y-2', className)}>
			{Array.from({ length: rows }, (_, index) => `skeleton-row-${index}`).map((id) => (
				<Skeleton key={id} className="h-8" />
			))}
			<span className="sr-only">{label}</span>
		</div>
	)
}
